/* Shared, page-local favourite state. Every save is still verified by the Worker. */
(function () {
  'use strict';
  if (window.ApolosFavourites) return;

  var entries = new Map();
  var sessionVersion = 0;
  var EVENT_SOURCE = 'apolos-favourites';

  function entryFor(product) {
    var id = String(product.id || '');
    if (!/^[1-9]\d*$/.test(id) || !product.handle) return null;
    if (!entries.has(id)) {
      entries.set(id, {
        id: id, handle: product.handle, phase: 'unknown',
        favourited: false, customerId: '', expiresAt: 0,
        version: 0, interaction: 0, reading: null,
        clicking: false, saving: false, refreshAfterSave: false,
        cancelConfetti: null, views: new Set()
      });
    }
    return entries.get(id);
  }

  function render(state) {
    state.views.forEach(function (view) {
      var button = view.button;
      if (!button.isConnected) { state.views.delete(view); return; }
      button.classList.toggle('is-active', state.favourited);
      button.setAttribute('aria-pressed', state.favourited ? 'true' : 'false');
      button.setAttribute('aria-label', state.favourited ? 'Remove from favourites' : 'Add to favourites');
      button.setAttribute('aria-busy', state.clicking || state.saving ? 'true' : 'false');
      button.disabled = state.clicking || state.saving;
    });
  }

  function applyResponse(state, data) {
    state.phase = data ? 'ready' : 'guest';
    state.favourited = !!(data && data.favourited);
    state.customerId = data ? data.customer.id : '';
    state.expiresAt = data ? data.expires_at : 0;
    render(state);
  }

  async function request(state, operation, payload) {
    if (!window.ApolosSession || typeof window.ApolosSession.accountHub !== 'function') {
      throw new Error('The account service did not load. Please reload the page.');
    }
    var data = await window.ApolosSession.accountHub(operation, Object.assign({
      product_id: state.id, handle: state.handle
    }, payload));
    if (data && (typeof data.favourited !== 'boolean' || !data.customer ||
        !data.customer.id || !Number.isSafeInteger(data.expires_at))) {
      throw new Error('The favourites response was incomplete.');
    }
    return data;
  }

  function load(state, force) {
    // Desktop/mobile and early clicks share the same pending request.
    if (state.reading) return state.reading;
    if (state.saving) {
      if (force) state.refreshAfterSave = true;
      return Promise.resolve(state);
    }
    if (!force && (state.phase === 'ready' || state.phase === 'guest')) {
      return Promise.resolve(state);
    }
    var version = ++state.version;
    var session = sessionVersion;
    if (state.phase === 'unknown' || state.phase === 'error') state.phase = 'loading';
    var pending = request(state, 'favourite_status').then(function (data) {
      if (version === state.version && session === sessionVersion) applyResponse(state, data);
      return state;
    }).catch(function (error) {
      if (version === state.version && session === sessionVersion &&
          state.phase !== 'ready' && state.phase !== 'guest') {
        state.phase = 'error';
      }
      throw error;
    }).finally(function () {
      if (state.reading === pending) state.reading = null;
    });
    state.reading = pending;
    return pending;
  }

  function preload(state, force) {
    return load(state, force).catch(function () {
      // Clicking retries a failed initial check and shows an actionable error.
    });
  }

  function stopConfetti(state) {
    if (state.cancelConfetti) state.cancelConfetti();
    state.cancelConfetti = null;
  }

  function confetti(button) {
    var wrap;
    var animations = [];
    var timer;
    function cleanup() {
      window.clearTimeout(timer);
      animations.forEach(function (animation) { animation.cancel(); });
      if (wrap) wrap.remove();
    }
    try {
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return cleanup;
      wrap = document.createElement('span');
      wrap.className = 'apolos-favourite-confetti';
      wrap.setAttribute('aria-hidden', 'true');
      wrap.style.cssText = 'position:absolute;left:50%;top:50%;width:0;height:0;overflow:visible;pointer-events:none;z-index:5;';
      var colors = ['#7E5A3F', '#ffffff', '#e8d9cc', '#d7bfae', '#000000'];
      for (var i = 0; i < 14; i++) {
        var particle = document.createElement('i');
        if (typeof particle.animate !== 'function') { cleanup(); return cleanup; }
        var angle = (-90 + (Math.random() * 140 - 70)) * Math.PI / 180;
        var distance = 26 + Math.random() * 26;
        var x = Math.cos(angle) * distance;
        var y = Math.sin(angle) * distance;
        var rotation = Math.random() * 360;
        particle.style.cssText = 'position:absolute;left:0;top:0;border-radius:2px;pointer-events:none;' +
          'width:' + (4 + Math.random() * 4) + 'px;height:' + (7 + Math.random() * 9) + 'px;' +
          'background:' + colors[i % colors.length] + ';filter:drop-shadow(0 2px 6px rgba(0,0,0,.18));';
        wrap.appendChild(particle);
        animations.push(particle.animate([
          { transform: 'translate(-50%,-50%) rotate(' + rotation + 'deg) scale(.6)', opacity: 0 },
          { opacity: 1, offset: 0.08 },
          { transform: 'translate(calc(-50% + ' + x + 'px),calc(-50% + ' + y + 'px)) rotate(' + (rotation + 140) + 'deg) scale(1)', opacity: 0 }
        ], { duration: 520, delay: Math.random() * 60, easing: 'cubic-bezier(.2,.8,.2,1)', fill: 'both' }));
      }
      button.appendChild(wrap);
      timer = window.setTimeout(cleanup, 700);
    } catch (_) { cleanup(); }
    return cleanup;
  }

  function redirect(view) {
    if (view.button.isConnected) window.location.assign(view.loginUrl);
  }

  async function resolveChangedSession(state, view) {
    await load(state, true);
    if (state.phase === 'guest') redirect(view);
  }

  async function click(state, view) {
    if (state.clicking || state.saving || !view.button.isConnected) return;
    var interaction = ++state.interaction;
    var session = sessionVersion;
    var previousState = state.favourited;
    var customerId = state.customerId;
    var savingStarted = false;
    var current = function () {
      return interaction === state.interaction && session === sessionVersion;
    };
    state.clicking = true;
    render(state);

    try {
      var variantId = view.getVariantId ? view.getVariantId() : null;
      // This await is skipped once the page-load check has completed.
      if (state.phase !== 'ready' && state.phase !== 'guest') await load(state);
      if (!current()) {
        await resolveChangedSession(state, view);
        return;
      }
      if (state.phase === 'ready' && Date.now() >= state.expiresAt) applyResponse(state, null);
      if (state.phase === 'guest') { redirect(view); return; }
      if (state.phase !== 'ready') throw new Error('We could not check your account.');

      previousState = state.favourited;
      customerId = state.customerId;
      var nextState = !previousState;
      ++state.version; // An older background read cannot undo this click.
      state.reading = null;
      state.favourited = nextState;
      state.saving = true;
      savingStarted = true;
      render(state);
      stopConfetti(state);
      if (nextState) state.cancelConfetti = confetti(view.button);

      // No extra status request on a ready click. The Worker checks this save.
      var saved = await request(state, 'set_favourite', {
        favourited: nextState, title: view.title,
        variant_id: variantId == null || variantId === '' ? null : String(variantId)
      });
      if (!saved) {
        if (current()) {
          stopConfetti(state);
          state.saving = false;
          state.reading = null;
          state.phase = 'unknown';
        }
        await resolveChangedSession(state, view);
        return;
      }
      if (!current()) return;
      if (saved.customer.id !== customerId) {
        invalidate();
        return;
      }
      applyResponse(state, saved);
      if (!saved.favourited) stopConfetti(state);
      window.dispatchEvent(new CustomEvent('ap:favorites:changed', {
        detail: { source: EVENT_SOURCE, product_id: state.id, favourited: saved.favourited }
      }));
    } catch (error) {
      if (current()) {
        if (savingStarted) {
          stopConfetti(state);
          state.favourited = previousState;
          state.phase = 'error'; // Recheck before retrying an uncertain save.
        }
        render(state);
        if (view.button.isConnected) {
          window.alert('We could not update your favourites. Please try again.');
        }
      }
    } finally {
      if (current()) {
        state.clicking = false;
        state.saving = false;
        render(state);
        if (state.refreshAfterSave) {
          state.refreshAfterSave = false;
          preload(state, true);
        }
      }
    }
  }

  function invalidate() {
    ++sessionVersion;
    entries.forEach(function (state) {
      ++state.version;
      ++state.interaction;
      state.reading = null;
      state.clicking = false;
      state.saving = false;
      state.refreshAfterSave = false;
      stopConfetti(state);
      applyResponse(state, null);
      state.phase = 'unknown';
      preload(state, true);
    });
  }

  function refreshAll() {
    entries.forEach(function (state) { preload(state, true); });
  }

  window.ApolosFavourites = Object.freeze({
    prime: function (product) {
      var state = entryFor(product);
      return state ? preload(state, false) : Promise.resolve();
    },
    bind: function (options) {
      var button = options.button;
      if (!button || button.dataset.apolosFavouritesBound === '1') return;
      var state = entryFor(options);
      if (!state) { button.disabled = true; return; }
      button.dataset.apolosFavouritesBound = '1';
      button.dataset.productId = state.id;
      button.dataset.handle = state.handle;
      state.views.add(options);
      render(state);
      button.addEventListener('click', function (event) {
        event.preventDefault();
        return click(state, options);
      });
      preload(state, false);
    }
  });

  window.addEventListener('apolos:session-changed', invalidate);
  window.addEventListener('ap:favorites:changed', function (event) {
    if (event.detail && event.detail.source === EVENT_SOURCE) return;
    if (event.detail && event.detail.product_id) {
      var state = entries.get(String(event.detail.product_id));
      if (state) preload(state, true);
    } else refreshAll();
  });
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) refreshAll();
  });
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') refreshAll();
  });
})();